// src/hooks/useGeolocationSubscription.js
import { useEffect, useRef } from 'react';
import { useSocket } from '../context/SocketContext';

/**
 * @param {boolean} enabledPublish — отправлять свои coords?
 * @param {'trackAll'|'trackRoute'|null} trackMode — подписка на чужие coords
 * @param {number|null} routeId — ID маршрута для trackRoute
 * @param {(mode:string, enabled:boolean, reason?:string)=>void} onStatusChange
 */
export function useGeolocationSubscription(
  enabledPublish,
  trackMode = null,
  routeId = null,
  onStatusChange = () => {}
) {
  const socket = useSocket();
  const watchId = useRef(null);
  const lastCoords = useRef(null)
  const intervalId = useRef(null);
  const joined = useRef({ publish: false, track: false });

  // === Вспомогательные функции для работы с гео ===
  const disable = reason => {
    if (watchId.current != null) {
      navigator.geolocation.clearWatch(watchId.current);
      watchId.current = null;
    }
    onStatusChange('publish', false, reason);
  };

  const send = ({ coords }) => {
    socket.emit(
      'courier:location',
      {
        latitude: coords.latitude,
        longitude: coords.longitude,
        recordedAt: new Date().toISOString(),
        routeId,
      },
      err => err && disable(err)
    );
  };

   const startWatching = () => {
     if (!socket || watchId.current != null) return;
    // 1) Получаем и сохраняем координаты в ref, но не шлём сразу
    navigator.geolocation.getCurrentPosition(
      pos => { lastCoords.current = pos.coords },
      e => disable(e.message),
      { enableHighAccuracy: true }
    );
    watchId.current = navigator.geolocation.watchPosition(
      pos => { lastCoords.current = pos.coords },
       e => disable(e.message),
       { enableHighAccuracy: true, maximumAge: 5000 }
     );

    // 2) Запускаем интервал, который каждые 5 сек отправит последнюю позицию
    intervalId.current = setInterval(() => {
      if (lastCoords.current) {
        send({ coords: lastCoords.current });
      }
    }, 5000); // <- здесь можно поставить любой ваш интервал в мс
   };

   const stopWatching = () => {
     if (watchId.current != null) {
       navigator.geolocation.clearWatch(watchId.current);
       watchId.current = null;
     }
    if (intervalId.current != null) {
      clearInterval(intervalId.current);
      intervalId.current = null;
    }
   };

  // === JOIN / LEAVE комнат ===
  useEffect(() => {
    if (!socket) return;

    // — публикация своих координат
    if (enabledPublish && !joined.current.publish) {
      socket.emit('geo:subscribe', { mode: 'publish' }, err => {
        if (err) {
          onStatusChange('publish', false, err);
        } else {
          joined.current.publish = true;
          // сразу стартуем слежение
          startWatching();
        }
      });
    }

    if (!enabledPublish && joined.current.publish) {
      socket.emit('geo:unsubscribe', { mode: 'publish' });
      joined.current.publish = false;
      stopWatching();
    }

    // — трекинг чужих координат
    if (!enabledPublish && trackMode && !joined.current.track) {
      socket.emit('geo:subscribe', { mode: trackMode, routeId }, err => {
        if (err) {
          onStatusChange(trackMode, false, err);
        } else {
          joined.current.track = true;
        }
      });
    }
    if ((trackMode === null || enabledPublish) && joined.current.track) {
      socket.emit('geo:unsubscribe', {
        mode: joined.current.track === 'trackRoute' ? 'trackRoute' : 'trackAll',
        routeId,
      });
      joined.current.track = false;
    }
  }, [enabledPublish, trackMode, routeId, socket, onStatusChange]);

  // === Очистка при размонтировании ===
  useEffect(() => {
    return () => {
      stopWatching();
    };
  }, []);
}
